import { Chart as ChartJS, LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip, Title } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment/min/moment-with-locales";
import { Chart } from "react-chartjs-2";
import { Stack, ToggleButtonGroup, ToggleButton } from "react-bootstrap";
import RATooltip from "../RATooltip";
import { useState, useMemo } from "react";
import { Col } from "react-bootstrap";
import { daysPerspectives } from "../constants";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip, ChartDataLabels, Title);

export const getPerspectiveLabel = days => {
    if (!days) return "N/A";
    return (
        daysPerspectives.find(x => JSON.stringify(x.days) === JSON.stringify(days))?.perspective ??
        `Custom - ${days.map(x => moment().weekday(x).format("dd")).join(", ")}`
    );
};

const getTimeRangeLabel = formData => {
    const selectedDaypart = formData?.daypart?.[0];

    if (selectedDaypart?.start_time && selectedDaypart?.end_time) {
        return `${moment(selectedDaypart.start_time, "HH:mm:ss").format("LT")} - ${moment(selectedDaypart.end_time, "HH:mm:ss").format("LT")}`;
    }

    if (formData?.hourRange?.length === 2) {
        const [startHour, endHour] = formData.hourRange;
        const formattedStart = moment().hours(startHour).minutes(0).seconds(0).format("LT");
        const formattedEnd = moment()
            .hours(endHour === 24 ? 23 : endHour)
            .minutes(endHour === 24 ? 59 : 0)
            .seconds(endHour === 24 ? 59 : 0)
            .format("LT");

        return `${formattedStart} - ${formattedEnd}`;
    }

    return "N/A";
};

export default function HealthTrendChart({ formData, data, time, percentage, monthsLimit, setMonthsLimit, grouping, setGrouping }) {
    const formatValue = value => {
        const num = value;

        if (percentage) {
            return `${num}%`;
        }
        if (time) {
            return moment.utc(moment.duration(num, "s").asMilliseconds()).format("HH:mm:ss");
        }
        return num;
    };

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return null;

        const groupingKey = grouping === "week" ? "data_week" : "data_month";

        const firstMetric = data.find(m => m.data?.[groupingKey]?.length);

        if (!firstMetric) return null;

        const labels = firstMetric.data[groupingKey].map(x => moment(x.basis_week).format(grouping === "week" ? "L" : "MMM YYYY"));

        const datasets = data
            .filter(m => m.data?.[groupingKey])
            .map(m => ({
                type: "line",
                label: m.label,
                borderColor: m.color,
                backgroundColor: m.color,
                data: m.data[groupingKey].map(x => (x.value === 0 ? null : Number(x.value.toFixed(1)))),
                tension: 0.4,
            }));

        return { labels, datasets };
    }, [data, grouping]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        datasets: {
            line: {
                pointRadius: 4,
                pointHoverRadius: 4,
                borderWidth: 3,
                borderJoinStyle: "round",
                borderColor: "#7BA7CC",
                pointBackgroundColor: "white",
            },
        },
        layout: {
            padding: {
                left: -30,
            },
        },
        plugins: {
            title: {
                display: true,
                text: `${formData?.station[0]?.radio_name}`,
                font: { size: 18 },
                padding: { bottom: 5 },
            },
            subtitle: {
                display: true,
                text: `Time: ${getTimeRangeLabel(formData)} | Perspective: ${getPerspectiveLabel(formData?.days)}`,
                padding: { bottom: 5 },
            },

            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (item) {
                        return `${item.dataset.label}: ${formatValue(item.raw)}`;
                    },
                },
            },
            datalabels: {
                display: context => {
                    // Hide labels for full range
                    if (monthsLimit === 0) return false;

                    // Hide labels for 6 months + weekly grouping
                    if (monthsLimit === 6 && grouping === "week") return false;

                    return true;
                },
                color: "#142533",
                font: {
                    size: 12,
                    weight: "bold",
                },
                labels: {
                    index: {
                        align: "end",
                        anchor: "center",
                    },
                },
                formatter: value => formatValue(value),
            },
            legend: {
                position: "bottom",
                labels: { usePointStyle: true },
            },
        },
        scales: {
            y: {
                ticks: {
                    padding: 30,
                    callback: value => formatValue(value),
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                offset: true,
                beginAtZero: true,
            },
            x: {
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                offset: true,
            },
        },
        interaction: {
            mode: "index",
        },
    };

    return (
        <div>
            <Stack direction="horizontal" gap={2} className="flex-wrap mb-2 mt-mb-0">
                <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5">Group By:</span>
                <ToggleButtonGroup type="radio" name="tot-grouping" className="ra-switcher" onChange={val => setGrouping(val)} value={grouping}>
                    <ToggleButton id={`tot-week`} value={"week"} variant="ra-dark-blue-outline" className=" align-content-center">
                        Week
                    </ToggleButton>
                    <ToggleButton id={`tot-month`} value={"month"} variant="ra-dark-blue-outline" className="d-flex align-items-center column-gap-1">
                        Month
                        <RATooltip size={18}>Monthly data includes all weeks starting in named month.</RATooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
                <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5 ms-auto">Limit:</span>
                <ToggleButtonGroup type="radio" name="month-limit" className="ra-switcher" onChange={val => setMonthsLimit(val)} value={monthsLimit}>
                    <ToggleButton id={`months-limit-3`} value={3} variant="ra-dark-blue-outline" className="align-content-center text-nowrap">
                        3 Months
                    </ToggleButton>
                    <ToggleButton id={`months-limit-6`} value={6} variant="ra-dark-blue-outline" className=" align-content-center text-nowrap">
                        6 Months
                    </ToggleButton>
                    <ToggleButton id={`months-limit-0`} value={0} variant="ra-dark-blue-outline" className=" align-content-center ">
                        Full
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            <Col>
                <div style={{ width: "100%", height: 400 }}>
                    <Chart data={chartData} options={chartOptions} />
                </div>
            </Col>
        </div>
    );
}
